import React, { useEffect, useMemo, useState } from 'react';
import { logAuditEvent } from '../../utils/auditLogger';

const STORAGE_KEY = 'orderFoodPackages';

const FOOD_ITEMS = [
  { id: 1, category: 'Breads & Rotis', name: 'Chapati', rate: 12, image: 'https://images.unsplash.com/photo-1619738332537-3d77b8f0f6d8?q=80&w=600&auto=format&fit=crop' },
  { id: 2, category: 'Breads & Rotis', name: 'Ghee Chapati', rate: 15, image: 'https://images.unsplash.com/photo-1626074353765-517a681e40be?q=80&w=600&auto=format&fit=crop' },
  { id: 3, category: 'Sabji', name: 'Paneer Masala', rate: 140, image: 'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?q=80&w=600&auto=format&fit=crop' },
  { id: 4, category: 'Sabji', name: 'Mix Veg Gravy', rate: 120, image: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?q=80&w=600&auto=format&fit=crop' },
  { id: 5, category: 'Dal & Rice', name: 'Dal Tadka', rate: 95, image: 'https://images.unsplash.com/photo-1589301760014-d929f3979dbc?q=80&w=600&auto=format&fit=crop' },
  { id: 6, category: 'Dal & Rice', name: 'Jeera Rice', rate: 85, image: 'https://images.unsplash.com/photo-1512058564366-18510be2db19?q=80&w=600&auto=format&fit=crop' },
  { id: 7, category: 'Dessert', name: 'Gulab Jamun', rate: 70, image: 'https://images.unsplash.com/photo-1601050690597-df0568f70950?q=80&w=600&auto=format&fit=crop' },
  { id: 8, category: 'Salad', name: 'Green Salad', rate: 50, image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=600&auto=format&fit=crop' }
];

const PERSON_OPTIONS = [10, 20, 30, 40, 50, 75, 100];

const formatAmount = (value) => {
  return Number(value || 0).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const createEmptyPackage = () => ({
  packageName: '',
  persons: 10,
  notes: ''
});

const OrderFoodPage = () => {
  const moduleName = 'Order Food';
  const [filterCategory, setFilterCategory] = useState('All');
  const [packageForm, setPackageForm] = useState(createEmptyPackage());
  const [selectedItems, setSelectedItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeCard, setActiveCard] = useState(null);

  useEffect(() => {
    logAuditEvent(moduleName, 'Accessed Module', 'Opened Order Food package page');
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setPackages(parsed);
        }
      } catch (error) {
        console.error('Failed to parse saved packages', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(packages));
  }, [packages]);

  const categoryOptions = useMemo(() => {
    const categories = Array.from(new Set(FOOD_ITEMS.map((item) => item.category)));
    return ['All', ...categories];
  }, []);

  const filteredItems = useMemo(() => {
    if (filterCategory === 'All') {
      return FOOD_ITEMS;
    }
    return FOOD_ITEMS.filter((item) => item.category === filterCategory);
  }, [filterCategory]);

  const selectedTotal = useMemo(() => {
    return selectedItems.reduce((sum, item) => sum + item.rate * item.qty, 0);
  }, [selectedItems]);

  const addItem = (item) => {
    logAuditEvent(moduleName, 'Add Item Click', JSON.stringify({ itemId: item.id, itemName: item.name }));
    setSelectedItems((prev) => {
      const existing = prev.find((x) => x.id === item.id);
      if (existing) {
        return prev.map((x) => {
          if (x.id === item.id) {
            return { ...x, qty: x.qty + 1 };
          }
          return x;
        });
      }
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const updateItemQty = (itemId, delta) => {
    setSelectedItems((prev) => {
      return prev
        .map((item) => {
          if (item.id !== itemId) {
            return item;
          }
          return { ...item, qty: item.qty + delta };
        })
        .filter((item) => item.qty > 0);
    });
  };

  const savePackage = () => {
    const packageName = packageForm.packageName.trim();
    if (!packageName || selectedItems.length === 0) {
      window.alert('Enter package name and add at least one food item.');
      return;
    }

    const record = {
      id: Date.now(),
      packageName,
      persons: Number(packageForm.persons),
      notes: packageForm.notes,
      items: selectedItems,
      totalAmount: selectedTotal,
      coverImage: selectedItems[0]?.image || FOOD_ITEMS[0].image,
      createdOn: new Date().toISOString()
    };

    setPackages((prev) => [record, ...prev]);
    setPackageForm(createEmptyPackage());
    setSelectedItems([]);
    logAuditEvent(moduleName, 'Save Package Click', JSON.stringify({ packageName: record.packageName, persons: record.persons }));
  };

  const applyCardChanges = () => {
    if (!activeCard) {
      return;
    }

    const updatedItems = activeCard.items.filter((item) => item.qty > 0);
    const updatedTotal = updatedItems.reduce((sum, item) => sum + item.rate * item.qty, 0);

    setPackages((prev) => prev.map((pkg) => {
      if (pkg.id !== activeCard.id) {
        return pkg;
      }
      return {
        ...pkg,
        persons: Number(activeCard.persons),
        items: updatedItems,
        totalAmount: updatedTotal
      };
    }));

    logAuditEvent(moduleName, 'Customize Package Save', JSON.stringify({ packageName: activeCard.packageName, persons: activeCard.persons }));
    setActiveCard(null);
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Order Food</h4>
          <p className="text-muted mb-0">Create thali/packages and show them as dashboard cards.</p>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
              <h5 className="mb-3">Create Package</h5>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Package Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. One Thali"
                    value={packageForm.packageName}
                    onChange={(event) => setPackageForm((prev) => ({ ...prev, packageName: event.target.value }))}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Persons</label>
                  <select
                    className="form-select"
                    value={packageForm.persons}
                    onChange={(event) => setPackageForm((prev) => ({ ...prev, persons: Number(event.target.value) }))}
                  >
                    {PERSON_OPTIONS.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={filterCategory}
                    onChange={(event) => setFilterCategory(event.target.value)}
                  >
                    {categoryOptions.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Optional"
                    value={packageForm.notes}
                    onChange={(event) => setPackageForm((prev) => ({ ...prev, notes: event.target.value }))}
                  />
                </div>
              </div>

              <div className="row g-3 mb-4">
                {filteredItems.map((item) => (
                  <div className="col-md-6" key={item.id}>
                    <div className="border rounded-3 p-2 d-flex gap-2 align-items-center">
                      <img src={item.image} alt={item.name} width="56" height="56" style={{ objectFit: 'cover', borderRadius: 8 }} />
                      <div className="flex-grow-1">
                        <div className="fw-semibold" style={{ fontSize: '0.92rem' }}>{item.name}</div>
                        <div className="text-muted" style={{ fontSize: '0.82rem' }}>{item.category}</div>
                        <div className="text-primary" style={{ fontSize: '0.85rem' }}>{formatAmount(item.rate)}</div>
                      </div>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => addItem(item)} type="button">Add</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border rounded-3 p-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Selected Items</h6>
                  <div className="fw-semibold">{formatAmount(selectedTotal)}</div>
                </div>
                {selectedItems.length === 0 ? (
                  <div className="text-muted">No items selected yet.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {selectedItems.map((item) => (
                      <div key={item.id} className="d-flex align-items-center justify-content-between border rounded-2 p-2 bg-white">
                        <div>
                          <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{item.name}</div>
                          <small className="text-muted">{formatAmount(item.rate)} x {item.qty}</small>
                        </div>
                        <div className="btn-group btn-group-sm" role="group">
                          <button type="button" className="btn btn-outline-secondary" onClick={() => updateItemQty(item.id, -1)}>-</button>
                          <button type="button" className="btn btn-outline-secondary" onClick={() => updateItemQty(item.id, 1)}>+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-end mt-3">
                <button type="button" className="btn btn-primary" onClick={savePackage}>Create Package Card</button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4">
              <h5 className="mb-3">Dashboard Cards</h5>
              {packages.length === 0 ? (
                <div className="text-muted">No package cards yet. Create one from left side.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      className="card border-0 shadow-sm"
                      style={{ cursor: 'pointer', overflow: 'hidden' }}
                      onClick={() => {
                        logAuditEvent(moduleName, 'Package Card Click', JSON.stringify({ packageName: pkg.packageName }));
                        setActiveCard(JSON.parse(JSON.stringify(pkg)));
                      }}
                    >
                      <img src={pkg.coverImage} alt={pkg.packageName} style={{ height: 120, objectFit: 'cover' }} />
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1">{pkg.packageName}</h6>
                            <small className="text-muted">{pkg.items.length} items</small>
                          </div>
                          <span className="badge bg-success">{pkg.persons} pax</span>
                        </div>
                        <div className="mt-2 fw-semibold text-primary">{formatAmount(pkg.totalAmount)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {activeCard && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">{activeCard.packageName}</h5>
                  <small className="text-muted">Customize package and order quickly</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setActiveCard(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3" style={{ maxWidth: 220 }}>
                  <label className="form-label">Persons</label>
                  <select
                    className="form-select"
                    value={activeCard.persons}
                    onChange={(event) => setActiveCard((prev) => ({ ...prev, persons: Number(event.target.value) }))}
                  >
                    {PERSON_OPTIONS.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>

                <div className="d-flex flex-column gap-2">
                  {activeCard.items.map((item) => (
                    <div key={item.id} className="d-flex justify-content-between align-items-center border rounded-3 p-2">
                      <div>
                        <div className="fw-semibold">{item.name}</div>
                        <small className="text-muted">{formatAmount(item.rate)} per unit</small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setActiveCard((prev) => ({
                              ...prev,
                              items: prev.items
                                .map((x) => (x.id === item.id ? { ...x, qty: x.qty - 1 } : x))
                                .filter((x) => x.qty > 0)
                            }));
                          }}
                        >
                          -
                        </button>
                        <span className="fw-semibold">{item.qty}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setActiveCard((prev) => ({
                              ...prev,
                              items: prev.items.map((x) => (x.id === item.id ? { ...x, qty: x.qty + 1 } : x))
                            }));
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setActiveCard(null)}>Close</button>
                <button type="button" className="btn btn-primary" onClick={applyCardChanges}>Save & Order</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderFoodPage;
